import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import type { IconName } from '../Icon/types'
import { cx } from '../utils/cx'
import type { Step, StepListProps, StepState } from './types'
import './StepList.css'

const STATE_ICON: Record<StepState, IconName> = {
  pending: 'circle',
  progress: 'circle-half',
  done: 'check-circle',
  locked: 'lock',
}

function RowContent({ step }: { step: Step }) {
  return (
    <>
      <Icon name={STATE_ICON[step.state]} size={24} className="hp-steps__icon" />
      <span className="hp-steps__text">
        <span className="hp-steps__title">{step.title}</span>
        {step.subtitle && <span className="hp-steps__subtitle">{step.subtitle}</span>}
      </span>
    </>
  )
}

/** Lista de pasos (DESIGN.md §4): filas de 64 px con estado; colapsa en "Todo listo" cuando todo está hecho. */
export function StepList({ steps, onSelect, allDoneLabel, allDoneDetail, collapseWhenDone = true, className }: StepListProps) {
  const { t } = useTranslation()
  const allDone = steps.length > 0 && steps.every((s) => s.state === 'done')

  if (allDone && collapseWhenDone) {
    return (
      <div className={cx('hp-steps-done', className)} role="status">
        <Icon name="check-circle" size={24} />
        <span className="hp-steps-done__label">
          {allDoneLabel ?? t('v2.ds.stepList.todoListo')}
          {allDoneDetail && <span className="hp-steps-done__detail"> · {allDoneDetail}</span>}
        </span>
      </div>
    )
  }

  return (
    <ul className={cx('hp-steps', className)}>
      {steps.map((step) => {
        const locked = step.state === 'locked'
        const rowClass = cx('hp-steps__row', `hp-steps__row--${step.state}`)
        if (step.action) {
          const { label, onClick, variant = 'soft' } = step.action
          return (
            <li key={step.id} className={rowClass}>
              <RowContent step={step} />
              <button type="button" onClick={onClick} className={cx('hp-steps__action', `hp-steps__action--${variant}`)}>
                {label}
              </button>
            </li>
          )
        }
        if (locked || !onSelect) {
          return (
            <li key={step.id} className={rowClass}>
              <RowContent step={step} />
            </li>
          )
        }
        return (
          <li key={step.id} className={rowClass}>
            <button type="button" onClick={() => onSelect(step)} className="hp-steps__button">
              <RowContent step={step} />
              <Icon name="chevron-right" size={22} className="hp-steps__chevron" />
            </button>
          </li>
        )
      })}
    </ul>
  )
}
